import { cn } from "@/lib/utils";
import { XIcon } from "lucide-react";
import React from "react";
import { ReactNode, RefObject, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

interface ModalProps {
  title: string;
  children?: ReactNode;
  onDismiss: () => void;
  visible: boolean;
  offsetTop?: number;
  anchorRef?: RefObject<HTMLElement>;
  containerRef: RefObject<HTMLDivElement>;
  className?: string;
}

const ModalContainer: React.FC<ModalProps> = ({
  title,
  offsetTop,
  children,
  onDismiss,
  visible,
  anchorRef,
  className,
}) => {
  const [computedTop, setComputedTop] = useState(null);
  const [computedLeft, setComputedLeft] = useState(null);
  const minTop = 48;
  const maxOffsetFromBottom = 48;
  const leftPadding = 16;
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && modalRef.current.contains(event.target as Node)) {
        return;
      }
      let node = event.target as HTMLElement | null;
      while (node) {
        if (
          node.getAttribute &&
          node.getAttribute("data-inside-modal") === "true"
        ) {
          return;
        }
        node = node.parentElement;
      }
      onDismiss();
    };

    const calculateTopOffset = () => {
      if (anchorRef?.current && modalRef.current) {
        const anchorRect = anchorRef.current.getBoundingClientRect();
        const portalRoot = document.getElementById("styling-panel-portal-root");
        const container = portalRoot?.parentElement;

        if (container) {
          const containerRect = container.getBoundingClientRect();
          const containerHeight = containerRect.height;
          const modalHeight = modalRef.current.offsetHeight;
          const modalWidth = modalRef.current.offsetWidth;

          // Position modal to the left of the right most edge of the container
          const left = containerRect.right - modalWidth;

          const maxTop = containerHeight - modalHeight - maxOffsetFromBottom;
          let top = anchorRect.top;

          // Apply min and max constraints relative to the container
          top = Math.max(top, minTop);
          top = Math.min(top, maxTop);

          // @ts-expect-error TS2345
          setComputedTop(top);
          // @ts-expect-error TS2345
          setComputedLeft(left - leftPadding);
        } else {
          setComputedTop(null);
          setComputedLeft(null);
        }
      }
    };

    if (visible) {
      document.addEventListener("mousedown", handleClickOutside);
      calculateTopOffset();
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [visible, onDismiss, anchorRef]);

  if (!visible) {
    return null;
  }

  const top = computedTop || 48;
  return ReactDOM.createPortal(
    <div
      className={cn(
        "fixed bg-blue-200 z-[100000] w-[400px] bg-secondary-0 rounded-md p-4 border-secondary-500 border text-xs flex flex-col gap-y-8",
        className
      )}
      style={{
        top: top + "px",
        left: computedLeft + "px",
      }}
      ref={modalRef}
    >
      <div className="flex flex-row justify-between items-center">
        <div className="text-xs font-semibold text-white">{title}</div>
        <XIcon
          className="cursor-pointer text-secondary-600 hover:text-secondary-800 w-4 h-4"
          onClick={() => {
            onDismiss();
          }}
        />
      </div>
      <div>{children}</div>
    </div>,
    // @ts-expect-error TS2345
    document.getElementById("styling-panel-portal-root")
  );
};

export default React.memo(ModalContainer);
